import router from './router'
import type { RouteRecordRaw } from 'vue-router'
import { isRelogin } from '@/config/axios/service'
import { getAccessToken } from '@/utils/auth'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { useUserStoreWithOut } from '@/store/modules/user'
import { usePermissionStoreWithOut } from '@/store/modules/permission'

const { start, done } = useNProgress()

const { loadStart, loadDone } = usePageLoading()

const parseURL = (
  url: string | null | undefined
): { basePath: string; paramsObject: { [key: string]: string } } => {
  if (url === null || url === undefined) {
    return { basePath: '', paramsObject: {} }
  }

  const questionMarkIndex = url.indexOf('?')
  let basePath = url
  const paramsObject: { [key: string]: string } = {}

  if (questionMarkIndex !== -1) {
    basePath = url.substring(0, questionMarkIndex)

    const queryString = url.substring(questionMarkIndex + 1)

    const searchParams = new URLSearchParams(queryString)
    searchParams.forEach((value, key) => {
      paramsObject[key] = value
    })
  }

  return { basePath, paramsObject }
}

// 路由不重定向白名单
const whiteList = [
  '/login',
  '/forgot-password',
  '/social-login',
  '/auth-redirect',
  '/bind',
  '/register',
  '/oauthLogin/gitee'
]

/** 公开分享页按前缀放行，跳过后台会话初始化。 */
const publicPrefixList = ['/share/']

/**
 * 剔除与已注册（静态）路由重名的动态路由节点，含嵌套子节点。
 * addRoute 遇到同名会以新记录替换旧记录：后端菜单里也下发了「查看角色 / 查看账号」
 * 这类详情页节点时，会把静态壳（RoleDetailShell / AccountDetailShell）里的同名子
 * 路由挪到别处，壳本身变成没有有效子记录的空壳。此后 SPA 跳转解析该路径只匹配到
 * 壳、取不到组件，页面就是空白；而刷新时匹配发生在动态路由替换之前，反而正常。
 * 这里静态优先，保证详情页在任何菜单下发组合下都能命中。
 */
const dropRoutesWithExistingNames = (routes: AppRouteRecordRaw[]): AppRouteRecordRaw[] =>
  routes
    .filter((route) => !(route.name && router.hasRoute(route.name)))
    .map((route) => {
      if (route.children?.length) {
        route.children = dropRoutesWithExistingNames(route.children)
      }
      return route
    })

// 路由加载前
router.beforeEach(async (to, from, next) => {
  start()
  loadStart()
  if (publicPrefixList.some((prefix) => to.path.startsWith(prefix))) {
    next()
    return
  }
  if (getAccessToken()) {
    if (to.path === '/login') {
      next({ path: '/' })
    } else {
      const userStore = useUserStoreWithOut()
      const permissionStore = usePermissionStoreWithOut()
      if (!userStore.getIsSetUser) {
        isRelogin.show = true
        await userStore.setUserInfoAction()
        isRelogin.show = false
        // 后端过滤菜单
        await permissionStore.generateRoutes()
        const addRouters = dropRoutesWithExistingNames(permissionStore.getAddRouters)
        addRouters.forEach((route) => {
          router.addRoute(route as unknown as RouteRecordRaw) // 动态添加可访问路由表
        })
        const redirectPath = from.query.redirect || to.path
        // 修复跳转时不带参数的问题
        const redirect = decodeURIComponent(redirectPath as string)
        const { paramsObject: query } = parseURL(redirect)
        // 仅当本次导航落在默认首页时，才按登录页的 ?redirect= 恢复原目标页。
        // 其余情况以本次导航目标为准（如登录后程序化跳转改密页 /change-password，
        // 不能被 ?redirect= 劫持回原页面）。
        const nextData =
          to.path === redirect || to.path !== '/'
            ? { ...to, replace: true }
            : { path: redirect, query }
        next(nextData)
      } else {
        next()
      }
    }
  } else {
    if (whiteList.indexOf(to.path) !== -1) {
      next()
    } else {
      next(`/login?redirect=${to.fullPath}`) // 否则全部重定向到登录页
    }
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done() // 结束Progress
  loadDone()
})
